import React from "react";

import { TextField, TextFieldProps } from "nikkei-ui";

import usePasswordField from "@/components/ui/Form/PasswordField/usePasswordField";

type PasswordFieldProps = {
  label: string;
  validationMessage?: string | undefined;
  trailingIconLabel: {
    on: string;
    off: string;
  };
  isLeadingIcon?: boolean;
  labelVariant?: "head" | "float";
  variant?: "password" | "newPassword";
  disabled?: boolean;
  children?: React.ReactNode;
} & Omit<TextFieldProps, "trailingIconLabel" | "variant">;

/**
 * PasswordField
 *
 * @example
 * ```ts
 *  <PasswordField />
 * ```
 * @param __namedParameters PasswordFieldProps
 * @returns JSX.Element
 * @category ReactComponents
 */
export const PasswordField = React.forwardRef<
  HTMLInputElement,
  PasswordFieldProps
>(
  (
    {
      label,
      validationMessage = "",
      disabled = false,
      trailingIconLabel,
      isLeadingIcon = true,
      labelVariant = "float",
      variant = "password",
      children,
      status,
      size,
      ...rest
    },
    ref,
  ) => {
    const { isRevealedPassword, toggleRevealed } = usePasswordField();

    return (
      <TextField
        {...rest}
        disabled={disabled}
        labelVariant={labelVariant}
        variant={isRevealedPassword ? `${variant}Revealed` : variant}
        label={label}
        status={status}
        size={size}
        validationMessage={validationMessage}
        leadingIcon={isLeadingIcon ? "Key" : "None"}
        trailingIcon={isRevealedPassword ? "Eye" : "EyeOff"}
        onIconClick={() => toggleRevealed()}
        trailingIconLabel={
          isRevealedPassword ? trailingIconLabel.off : trailingIconLabel.on
        }
        ref={ref}
      >
        {children}
      </TextField>
    );
  },
);

PasswordField.displayName = "PasswordField";
export default PasswordField;
